import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-route SEO head tags.
 *
 * The app is a client-rendered SPA, so the static tags in public/index.html are
 * the same for every URL. This component lets each public page override the
 * title/description/canonical (and social cards) at runtime via react-helmet,
 * so crawlers that execute JS and in-app navigation both get page-specific meta.
 *
 * Props:
 *   title       — page <title> (a " — NuVe System" suffix is appended unless
 *                 the title already mentions NuVe).
 *   description — meta description + og/twitter description.
 *   path        — absolute path for the canonical + og:url (e.g. "/get-started").
 *   image       — absolute or root-relative og/twitter image URL.
 *   noindex     — when true, emit robots "noindex, follow" (for app/auth pages).
 */
const SITE_NAME = 'NuVe System';
const ORIGIN = 'https://nuvesystem.com';
const DEFAULT_DESCRIPTION =
  'Professional business management software for tracking client data, organizing customer bases, and overseeing daily business activity.';
const DEFAULT_IMAGE = `${ORIGIN}/logo512.png`;

function absoluteUrl(value, fallback) {
  if (!value) return fallback;
  if (/^https?:\/\//i.test(value)) return value;
  return `${ORIGIN}${value.startsWith('/') ? '' : '/'}${value}`;
}

const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image,
  noindex = false,
}) => {
  const fullTitle =
    !title || /nuve/i.test(title) ? title || SITE_NAME : `${title} — ${SITE_NAME}`;
  const canonical = absoluteUrl(path, `${ORIGIN}/`);
  const ogImage = absoluteUrl(image, DEFAULT_IMAGE);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? 'noindex, follow' : 'index, follow'}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter / X */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export default Seo;
